import { Badge, Box, Flex, type BoxProps } from "@chakra-ui/react";
import { type ReactNode } from "react";

interface SelectableCardProps extends Omit<BoxProps, "children"> {
  isSelected: boolean;
  isDisabled?: boolean;
  disabledLabel?: string;
  // Rendered as a <button>, so default to "button" to avoid submitting a surrounding form on select.
  type?: "button" | "submit" | "reset";
  children: ReactNode;
}

export const SelectableCard = ({
  isSelected,
  isDisabled = false,
  disabledLabel,
  type = "button",
  children,
  ...boxProps
}: SelectableCardProps) => (
  <Box
    as="button"
    type={type}
    borderWidth="2px"
    borderRadius="lg"
    borderColor={isSelected ? "smPrimarySoft" : "smBorder"}
    bg={isDisabled ? "smBackgroundSecondary" : isSelected ? "smPrimarySofter" : "smBackground"}
    p="4"
    textAlign="left"
    transition="all 0.15s ease"
    opacity={isDisabled ? 0.6 : 1}
    cursor={isDisabled ? "not-allowed" : "pointer"}
    _hover={!isDisabled ? { borderColor: "smPrimarySoft", shadow: "sm" } : {}}
    {...boxProps}
  >
    {isDisabled && disabledLabel ? (
      // The badge shares the row rather than floating over it: a reserved padding only fits the
      // label it was measured against, and the longer ones then run across the title.
      <Flex gap="3" align="flex-start" justify="space-between">
        <Box minW="0">{children}</Box>
        <Badge colorScheme="gray" fontSize="2xs" variant="subtle" whiteSpace="normal" textAlign="right">
          {disabledLabel}
        </Badge>
      </Flex>
    ) : (
      children
    )}
  </Box>
);
